import { buildIframeSrc } from "./params";

// The form's admin configures a destination that is theirs, not ours, so a
// relative value means a page on the site the form is embedded in. Resolving
// happens here rather than in the frame for exactly that reason: the frame is
// served from the forms origin, where "/thank-you" would resolve to the wrong
// site entirely. The message carries the configured string verbatim.
//
// Restricting the protocol is what stops a stored javascript: URL from becoming
// script running on the customer's page. `new URL` throws on malformed input,
// and nothing may throw past this script onto that page.
function resolveRedirect(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  try {
    const url = new URL(value, document.baseURI);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      console.warn(
        `aivry.js: ignoring redirect to "${value}"; only http and https are followed`,
      );
      return null;
    }
    return url.href;
  } catch {
    console.warn(`aivry.js: ignoring unparseable redirect to "${value}"`);
    return null;
  }
}

// Messages come from our own form page (apps/marketing/app/forms/[embedKey]/
// components/) on the shared "aivry:event" envelope: `resize` carries a height
// and, from a form page that can measure it, the widest the form needs to be,
// both from use-iframe-height-reporter.ts; `form-submit` reports a successful
// submission from notify-form-submitted.ts; and `page-change` says the form
// moved to another page, from notify-page-change.ts, with where the form's top
// sits inside the frame. All three shapes are pinned by fixtures in contract/,
// which the tests on either side of the boundary assert against.

// Set on every frame a widget renders or takes over. data-aivry-ready cannot do
// this job alone: a page that saves a popup's HTML and rebuilds the popup later,
// as Elementor does, copies the attribute onto a frame no widget is listening
// to. A property is not part of the HTML, so a rebuilt copy does not inherit it,
// and Symbol.for gives every copy of this bundle on a page the same key.
export const SEEN = Symbol.for("aivry.seen");
export type SeenFrame = HTMLIFrameElement & { [SEEN]?: true };

// Every src a widget has set on a frame during this page load, which is what
// says whether a marked frame is this page's own rebuilt copy. Kept on window
// under Symbol.for so every copy of this bundle on a page shares it, as SEEN is.
const BUILT = Symbol.for("aivry.built");

function builtSrcs(): Set<string> {
  const page = window as Window & { [BUILT]?: Set<string> };
  return (page[BUILT] ??= new Set());
}

// How long a frame stays invisible after its page has loaded, waiting for the
// first size. Counted from the frame's load event rather than from this script
// running: the snippet's iframe is loading="lazy", so a form below the fold
// makes no request until the visitor scrolls near it, and a timer started here
// would reveal an empty frame long before then. Comfortably more than a loaded
// form page takes to hydrate and report, so a visitor never sees the fallback
// on a working form; short enough that one whose form page never reports is
// not left looking at nothing.
const REVEAL_FALLBACK_MS = 3000;

export class FormsWidget {
  readonly iframe: HTMLIFrameElement;
  private readonly formOrigin: string;
  private hasMeasured = false;
  // Whether the snippet sized or placed the frame itself: a width other than
  // the default 100% of the column, by attribute or style, a max-width, a
  // margin, or a display other than block, which is the one centering needs.
  // Read once, before any resize rewrites the style.
  private readonly snippetPlacesFrame: boolean;
  // The frame is kept invisible until its first size arrives, so the visitor
  // never sees it painted at the snippet's full width and placeholder height
  // and then snap to the form. Invisible, not absent: the form page inside
  // still loads and measures itself. The timer is the fallback that shows the
  // frame anyway if no size comes once its page has loaded, since every reason
  // the form page might never report, a stale bundle, a hydration error, must
  // not leave an invisible form.
  private hidden = false;
  private revealTimer: ReturnType<typeof setTimeout> | undefined;

  // The host is the iframe the snippet wrote: the form the visitor gets when
  // this script is blocked (ad blockers, customer CSPs), and the specification
  // for the one it gets when it isn't. Its title, dimensions and position are
  // the customer's to set, so they are copied rather than imposed here.
  //
  // A copy is swapped in rather than the host's own src being rewritten. Both
  // load the parameterised URL, but re-pointing a frame that has already
  // finished loading adds an entry to the visitor's session history, so the
  // back button costs them an extra press and returns them to this same form
  // without its attribution. Whether the host has got that far by now depends
  // on their connection and where the form sits on the page; setting the src
  // before the frame is inserted takes the race away.
  //
  // A host already marked data-aivry-ready is a frame this script rendered that
  // the page has since rebuilt from its own saved HTML, as Elementor does with a
  // popup each time a visitor opens it. When its src is one this page load
  // built, the copy is already loading the right form with the right params,
  // so it is taken over where it stands: swapping it would load the form a
  // second time. That still holds after a page tidies the ad params out of its
  // address bar, since the copy rightly carries the ones it was built with.
  // Any other marked frame, most likely copied out of DevTools and pasted as
  // the snippet, is rebuilt like one, so its leads are not credited to the ad
  // of the visit it was copied from, even when that visit was to this page.
  constructor(host: HTMLIFrameElement, formUrl: string) {
    const src = buildIframeSrc(
      formUrl,
      window.location.search,
      window.location.href,
      document.referrer,
    );
    const marked = host.hasAttribute("data-aivry-ready");
    const hostSrc = host.getAttribute("src");
    const rebuilt = marked && hostSrc !== null && builtSrcs().has(hostSrc);
    this.iframe = rebuilt ? host : (host.cloneNode(false) as HTMLIFrameElement);
    if (!rebuilt) {
      this.iframe.src = src;
      builtSrcs().add(src);
      // Not copied from the host like the rest: without it the iframe request
      // carries the landing page's full URL in the Referer header whenever that
      // page opts into a lax policy (a plain <meta name="referrer"
      // content="unsafe-url"> is enough), which would put its query string into
      // our access logs and undo the stripping done in params.ts. Origin only is
      // what the embed's allowedOrigins check needs. A rebuilt frame carries it
      // over from the one this set it on.
      this.iframe.referrerPolicy = "strict-origin-when-cross-origin";
    }
    const { width, maxWidth, marginLeft, marginRight, display } =
      this.iframe.style;
    this.snippetPlacesFrame =
      this.iframe.getAttribute("width") !== "100%" ||
      width !== "" ||
      maxWidth !== "" ||
      marginLeft !== "" ||
      marginRight !== "" ||
      (display !== "" && display !== "block");
    // A marked frame arrives hidden by this script's pass over the frame it was
    // copied from, and still waits on its first size like any other.
    const { visibility } = this.iframe.style;
    if (visibility === "" || (marked && visibility === "hidden")) {
      this.iframe.style.visibility = "hidden";
      this.hidden = true;
      this.iframe.addEventListener("load", this.onLoad);
    }
    // Claims the frame, so a second pasted snippet's copy of this bundle scans
    // past it instead of rebuilding a form that is already loading. The
    // attribute says this script built the frame's src; SEEN says the frame
    // has a widget, which a rebuilt copy does not.
    this.iframe.dataset.aivryReady = "";
    (this.iframe as SeenFrame)[SEEN] = true;

    this.formOrigin = new URL(formUrl).origin;

    if (!rebuilt) host.replaceWith(this.iframe);

    // Registered last: a throwing replaceWith must not leave a listener
    // registered for a never-inserted iframe.
    window.addEventListener("message", this.onMessage);
  }

  destroy(): void {
    clearTimeout(this.revealTimer);
    this.iframe.removeEventListener("load", this.onLoad);
    window.removeEventListener("message", this.onMessage);
    this.iframe.remove();
  }

  // The page inside has loaded, so a working form page reports from here
  // within a moment. A frame that loads again later has either been revealed
  // already or has its timer running.
  private onLoad = (): void => {
    if (!this.hidden || this.revealTimer !== undefined) return;
    this.revealTimer = setTimeout(() => this.reveal(), REVEAL_FALLBACK_MS);
  };

  private onMessage = (event: MessageEvent): void => {
    if (event.source !== this.iframe.contentWindow) return;
    if (event.origin !== this.formOrigin) return;
    const data = event.data as {
      type?: unknown;
      event?: unknown;
      payload?: {
        height?: unknown;
        maxWidth?: unknown;
        redirectUrl?: unknown;
        top?: unknown;
      };
    } | null;
    if (!data || data.type !== "aivry:event") return;

    if (data.event === "form-submit") {
      const destination = resolveRedirect(data.payload?.redirectUrl);
      if (destination) window.location.assign(destination);
      return;
    }

    if (data.event === "page-change") {
      this.bringFormTopIntoView(data.payload?.top);
      return;
    }

    if (data.event !== "resize") return;
    const height = data.payload?.height;
    if (typeof height !== "number" || !Number.isFinite(height) || height <= 0)
      return;
    this.iframe.style.height = `${Math.ceil(height)}px`;

    // The snippet makes the frame as wide as its column, and the form inside
    // stops at its own width, centered, so a wide column showed a narrow form
    // in a wide empty frame. Capping the frame at the form's width and centering
    // it keeps the form where it was and gives the empty space back to the
    // page. A narrower column still shrinks the frame. The two go together: a
    // capped frame that is not centered moves the form to the frame's left
    // edge, so where the frame cannot be centered it is not capped either.
    // Left alone when the form sends no width, which a form page older than
    // this script does not, and when the snippet's own style has sized or
    // placed the frame, which the README promises is kept.
    const maxWidth = data.payload?.maxWidth;
    if (
      typeof maxWidth === "number" &&
      Number.isFinite(maxWidth) &&
      maxWidth > 0 &&
      !this.snippetPlacesFrame
    ) {
      this.iframe.style.maxWidth = `${Math.ceil(maxWidth)}px`;
      this.iframe.style.display = "block";
      this.iframe.style.marginLeft = "auto";
      this.iframe.style.marginRight = "auto";
    }

    if (!this.hasMeasured) {
      this.hasMeasured = true;
      // The frame is revealed in this same tick, so its first height must land
      // whole: with the transition already on it the visitor would see the
      // frame appear at the placeholder height and grow. Reading the layout
      // commits the height before the transition exists; it then eases every
      // later change, which is the page changes the visitor does watch.
      void this.iframe.offsetHeight;
      this.iframe.style.transition = "height 150ms ease-out";
    }
    this.dropScrollbarIfFormFits(height);
    this.reveal();
  };

  // Only once a real measurement has arrived. Until then the frame keeps its
  // own scrollbar, because every reason the child might never report — a
  // published bundle newer than the marketing deploy, a hydration error
  // inside the frame, a stale bundle still cached on a visitor's machine —
  // would otherwise clip a form taller than the initial height with no way
  // to reach its submit button.
  //
  // And only once the frame has actually grown to the form. The landing page's
  // own CSS can hold the frame shorter, a fixed height with !important for
  // instance, and the scrollbar is then the visitor's only way to the submit
  // button, so it is given back whenever a page of the form no longer fits.
  // A height that is still easing reads as too short, so the frame is judged
  // once its own height transition has run, not any animation the page puts
  // on it.
  private dropScrollbarIfFormFits(height: number): void {
    const easing = (this.iframe.getAnimations?.() ?? []).filter(
      (animation) =>
        (animation as CSSTransition).transitionProperty === "height",
    );
    if (easing.length > 0) {
      Promise.all(easing.map((animation) => animation.finished)).then(
        () => this.setScrollbarFor(height),
        // Cancelled by a later height, which judges the frame itself.
        () => {},
      );
      return;
    }
    this.setScrollbarFor(height);
  }

  // A frame with no height on screen is not laid out, in a closed popup for
  // instance, and nothing can be said yet about the height the page will give
  // it, so it is left as it is: a scrollbar costs nothing on a form that fits.
  private setScrollbarFor(height: number): void {
    const shown = this.iframe.clientHeight;
    if (shown === 0) return;
    if (shown < Math.ceil(height)) {
      this.iframe.removeAttribute("scrolling");
    } else {
      this.iframe.setAttribute("scrolling", "no");
    }
  }

  // The form moved to another page and the frame has already scrolled itself to
  // the top. What it cannot do is bring itself back on screen when the visitor
  // clicked Next at the bottom of a long page and the form's top is now above
  // the viewport, so it says where that top sits inside the frame and the host
  // decides. A top already on screen moves nothing, which is what keeps the
  // landing page still on every other click; scrollIntoView from inside the
  // frame moved it on all of them.
  private bringFormTopIntoView(offset: unknown): void {
    if (typeof offset !== "number" || !Number.isFinite(offset) || offset < 0)
      return;
    const top = this.iframe.getBoundingClientRect().top + offset;
    if (top >= 0) return;
    window.scrollBy({ top });
  }

  private reveal(): void {
    if (!this.hidden) return;
    this.hidden = false;
    clearTimeout(this.revealTimer);
    this.revealTimer = undefined;
    this.iframe.removeEventListener("load", this.onLoad);
    this.iframe.style.visibility = "";
  }
}
